import React, { useState, useEffect, useRef } from 'react';
import { Form, Button } from 'react-bootstrap';
import UkiesAssets from '../Admin/UkiesAssets';

export default function UkiesPhotoText({ componentSettings, setComponentSettings, onChange, selectedComponent, setSelectedComponent }) {
    const [showAssetSelector, setShowAssetSelector] = useState(false);
    const [isInitialized, setIsInitialized] = useState(false);
    const [textSourceCodeMode, setTextSourceCodeMode] = useState(false);
    
    const textEditorRef = useRef(null);
    const apiToken = process.env.REACT_APP_UKIES_API_TOKEN;
    
    // Get current values with defaults
    const format = componentSettings.format || 'normal';
    const overlay_background_color = componentSettings.overlay_background_color || 'none';
    const background_image = componentSettings.background_image || '';
    const background_position_X = componentSettings.background_position_X || 50;
    const background_position_Y = componentSettings.background_position_Y || 50;
    const paddingTop = componentSettings.padding_top !== undefined ? componentSettings.padding_top : (format === 'normal' ? true : false);
    const paddingBottom = componentSettings.padding_bottom !== undefined ? componentSettings.padding_bottom : (format === 'normal' ? true : false);
    const title = componentSettings.title || '';
    const text_content = componentSettings.text_content || '';
    const button_label = componentSettings.button_label || '';
    const button_link = componentSettings.button_link || '';
    const button_color = componentSettings.button_color || 'default';

    // Overlay background color options
    const overlayColorOptions = [
        { value: 'none', label: 'Geen', color: '#00000000' },
        { value: 'black50', label: 'Zwart 50', color: '#00000050' },
        { value: 'black90', label: 'Zwart 90', color: '#00000090' },
        { value: 'green', label: 'Groen', color: '#3b4f4690' },
        { value: 'orange', label: 'Oranje', color: '#d1911250' }
    ];

    // Parse HTML content to extract photo-text configuration
    const parseHTMLContent = (htmlContent) => {
        if (!htmlContent) return null;
        
        try {
            const parser = new DOMParser();
            const doc = parser.parseFromString(htmlContent, 'text/html');
            
            // Look for either .structure or .ukies-photo-text as the main container
            const mainDiv = doc.querySelector('.structure, .ukies-photo-text');
            if (!mainDiv) return null;
            
            let config = {
                format: 'normal',
                overlay_background_color: 'none',
                background_image: '',
                background_position_X: 50,
                background_position_Y: 50,
                padding_top: true,
                padding_bottom: true,
                title: '',
                text_content: '',
                button_label: '',
                button_link: '',
                button_color: 'default'
            };
            
            // Extract format from main div classes
            if (mainDiv.classList.contains('structure-fullwidth')) {
                config.format = 'fullwidth';
                config.padding_top = false;
                config.padding_bottom = false;
            } else {
                config.format = 'normal';
                
                // Extract padding for normal format only
                const style = mainDiv.getAttribute('style') || '';
                if (style.includes('padding-top: 4rem')) {
                    config.padding_top = true;
                } else if (style.includes('padding-top: 0rem')) {
                    config.padding_top = false;
                }
                if (style.includes('padding-bottom: 4rem')) {
                    config.padding_bottom = true;
                } else if (style.includes('padding-bottom: 0rem')) {
                    config.padding_bottom = false;
                }
            }
            
            // Extract overlay background color
            const overlayDiv = doc.querySelector('.overlay-container');
            if (overlayDiv) {
                const style = overlayDiv.getAttribute('style') || '';
                const bgColorMatch = style.match(/background-color:\s*([^;]+)/);
                if (bgColorMatch) {
                    const color = bgColorMatch[1].trim();
                    // Find matching color option
                    const matchingOption = overlayColorOptions.find(opt => opt.color === color);
                    if (matchingOption) {
                        config.overlay_background_color = matchingOption.value;
                    }
                }
            }
            
            // Extract background image from component-background div
            const backgroundDiv = doc.querySelector('.component-background');
            if (backgroundDiv) {
                const style = backgroundDiv.getAttribute('style') || '';
                const bgImageMatch = style.match(/background-image:\s*url\(([^)]+)\)/);
                if (bgImageMatch) {
                    config.background_image = bgImageMatch[1].replace(/['"]/g, '');
                }
                
                // Extract background position
                const bgPositionMatch = style.match(/background-position:\s*(\d+)%\s+(\d+)%/);
                if (bgPositionMatch) {
                    config.background_position_X = parseInt(bgPositionMatch[1]);
                    config.background_position_Y = parseInt(bgPositionMatch[2]);
                }
            }
            
            // Extract title
            const titleEl = doc.querySelector('.header-text');
            if (titleEl) {
                config.title = titleEl.textContent.trim();
            }
            
            // Extract text content
            const textEl = doc.querySelector('.body-text');
            if (textEl) {
                config.text_content = textEl.innerHTML.trim();
            }
            
            // Extract button
            const buttonEl = doc.querySelector('.component-button button');
            if (buttonEl) {
                config.button_label = buttonEl.textContent.trim();
                config.button_color = buttonEl.classList.contains('highlight') ? 'highlight' : 'default';
            }
            
            const linkEl = doc.querySelector('.component-button a');
            if (linkEl) {
                config.button_link = linkEl.getAttribute('href') || '';
            }
            
            return config;
        } catch (error) {
            console.error('Error parsing HTML content:', error);
            return null;
        }
    };

    // HTML escaping function to prevent XSS
    const escapeHtml = (text) => {
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
    };

    // Generate HTML content from current form values
    const generateHTMLContent = () => {
        return generateHTMLContentFromSettings(componentSettings);
    };

    // Generate HTML content from provided settings object
    const generateHTMLContentFromSettings = (settings) => {
        const baseStructureClass = settings.format === 'fullwidth' ? 'structure-fullwidth' : 'structure structure-1';
        const structureClass = `${baseStructureClass} ukies-photo-text`;
        
        // Generate padding styles for normal format only
        let paddingStyles = '';
        if (settings.format !== 'fullwidth') {
            const ptop = settings.padding_top !== undefined ? settings.padding_top : paddingTop;
            const pbottom = settings.padding_bottom !== undefined ? settings.padding_bottom : paddingBottom;
            const paddingParts = [];
            paddingParts.push(`padding-top: ${ptop ? '4rem' : '0rem'}`);
            paddingParts.push(`padding-bottom: ${pbottom ? '4rem' : '0rem'}`);
            paddingStyles = ` style="${paddingParts.join('; ')}"`;
        }
        
        const overlayColor = settings.overlay_background_color || overlay_background_color;
        const overlayColorOption = overlayColorOptions.find(opt => opt.value === overlayColor) || overlayColorOptions[0];
        const overlayStyle = overlayColor !== 'none' ? ` style="background-color: ${overlayColorOption.color}"` : '';
        const bgImage = settings.background_image || background_image;
        const bgPosition = `${settings.background_position_X || 50}% ${settings.background_position_Y || 50}%`;
        const titleText = settings.title || title;
        const textContent = settings.text_content || text_content;
        const btnLabel = settings.button_label || button_label;
        const btnLink = settings.button_link || button_link;
        const btnColor = settings.button_color || button_color;
        
        // Escape user input for title (text_content is already HTML)
        const escapedTitle = escapeHtml(titleText);
        const escapedButtonLabel = escapeHtml(btnLabel);
        const escapedButtonLink = escapeHtml(btnLink);

        // Generate button HTML only if button_label is not empty
        const buttonHtml = btnLabel ? `			<div class="component component-button">
				<a href="${escapedButtonLink}">
					<button${btnColor === 'highlight' ? ' class="highlight"' : ''} type="button">${escapedButtonLabel}</button>
				</a>
			</div>` : '';

        return `<div class='${structureClass}'${paddingStyles}>
	<div class='component component-text component-background' style='background-image:url(${bgImage}); background-position: ${bgPosition}'>
		<div class="overlay-container"${overlayStyle}>
			<div class="structure center white flex flex-col justify-center py-4vw px-10vw gap-0">
				<div class="header-text">${escapedTitle}</div>
				<div class="body-text center">
					${textContent}
				</div>
${buttonHtml}
			</div>
		</div>
	</div>
</div>`;
    };

    // Initialize form values from HTML content on component mount
    useEffect(() => {
        if (selectedComponent?.content && !isInitialized) {
            const parsed = parseHTMLContent(selectedComponent.content);
            if (parsed) {
                setComponentSettings(prev => ({
                    ...prev,
                    format: parsed.format,
                    overlay_background_color: parsed.overlay_background_color,
                    background_image: parsed.background_image,
                    background_position_X: parsed.background_position_X,
                    background_position_Y: parsed.background_position_Y,
                    padding_top: parsed.padding_top,
                    padding_bottom: parsed.padding_bottom,
                    title: parsed.title,
                    text_content: parsed.text_content,
                    button_label: parsed.button_label,
                    button_link: parsed.button_link,
                    button_color: parsed.button_color
                }));
            }
            setIsInitialized(true);
        }
    }, [selectedComponent?.content, isInitialized, setComponentSettings]);

    // Reset initialization when component changes
    useEffect(() => {
        setIsInitialized(false);
    }, [selectedComponent?.id]);

    // Set initial content for text editor when data changes
    useEffect(() => {
        if (textEditorRef.current && text_content !== undefined) {
            const isFocused = document.activeElement === textEditorRef.current;
            
            if (!isFocused) {
                if (textSourceCodeMode) {
                    textEditorRef.current.value = text_content || '';
                } else {
                    textEditorRef.current.innerHTML = text_content || '<p>Voer hier de tekst in...</p>';
                }
            }
        }
    }, [text_content, textSourceCodeMode]);

    // Handle field changes and update HTML content
    const handleFieldChange = (fieldKey, value) => {
        // Immediately update component settings for UI responsiveness
        let updatedSettings = { ...componentSettings, [fieldKey]: value };
        
        // Handle padding when switching format
        if (fieldKey === 'format') {
            if (value === 'fullwidth') {
                updatedSettings.padding_top = false;
                updatedSettings.padding_bottom = false;
            } else if (value === 'normal') {
                // Set default padding to true for normal format
                updatedSettings.padding_top = true;
                updatedSettings.padding_bottom = true;
            }
        }
        
        // Generate content immediately for instant visual updates
        const newContent = generateHTMLContentFromSettings(updatedSettings);
        
        setComponentSettings(prev => ({
            ...prev,
            ...updatedSettings,
            content: newContent
        }));
        
        // Trigger onChange for all updated fields immediately
        onChange(fieldKey, value);
        if (fieldKey === 'format') {
            if (value === 'fullwidth') {
                onChange('padding_top', false);
                onChange('padding_bottom', false);
            } else if (value === 'normal') {
                onChange('padding_top', true);
                onChange('padding_bottom', true);
            }
        }
        onChange('content', newContent);
    };

    // Handle asset selection for background image
    const handleAssetSelect = (asset) => {
        const imageUrl = asset.image_variations?.large 
            ? asset.image_variations.large.replace('/large', '/max')
            : asset.url;
        
        handleFieldChange('background_image', imageUrl);
        setShowAssetSelector(false);
    };

    // Format text functions for rich text editor
    const formatText = (command, value = null) => {
        document.execCommand(command, false, value);
        textEditorRef.current.focus();
    };

    // Handle text change from contentEditable
    const handleTextChange = (e) => {
        const content = e.target.innerHTML;
        if (content === '<p>Voer hier de tekst in...</p>') {
            handleFieldChange('text_content', '');
        } else {
            handleFieldChange('text_content', content);
        }
    };

    // Handle text editor click to clear placeholder
    const handleTextEditorClick = () => {
        if (textEditorRef.current && textEditorRef.current.innerHTML === '<p>Voer hier de tekst in...</p>') {
            textEditorRef.current.innerHTML = '<p><br></p>';
            const range = document.createRange();
            const selection = window.getSelection();
            range.setStart(textEditorRef.current.firstChild, 0);
            range.collapse(true);
            selection.removeAllRanges();
            selection.addRange(range);
        }
    };

    // Handle source code mode toggle
    const handleSourceCodeToggle = () => {
        if (textSourceCodeMode) {
            const sourceContent = textEditorRef.current.value;
            handleFieldChange('text_content', sourceContent);
        } else {
            const wysiwygContent = textEditorRef.current.innerHTML;
            handleFieldChange('text_content', wysiwygContent);
        }
        setTextSourceCodeMode(!textSourceCodeMode);
    };
    
    return (
        <div>
            <style jsx>{`
                .rich-text-editor {
                    font-family: inherit;
                    font-size: 0.9rem;
                    line-height: 1.5;
                }
                .rich-text-editor:focus {
                    border-color: #80bdff !important;
                    box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25) !important;
                }
                .rich-text-editor p {
                    margin-bottom: 0.5rem;
                }
                .rich-text-editor ul, .rich-text-editor ol {
                    margin-bottom: 0.5rem;
                    padding-left: 1.5rem;
                }
                .rich-text-editor a {
                    color: #007bff;
                    text-decoration: underline;
                }
            `}</style>
            
            {/* Photo Text Configuration Header */}
            <h6 className="mb-3 mt-3">
                Foto met tekst
            </h6>
            
            {/* Format Selection */}
            <Form.Group className="mb-3">
                <Form.Label>Formaat</Form.Label>
                <Form.Select
                    value={format}
                    onChange={(e) => handleFieldChange('format', e.target.value)}
                >
                    <option value="normal">Normaal</option>
                    <option value="fullwidth">Volle breedte</option>
                </Form.Select>
            </Form.Group>
            
            {/* Padding Options - only show for normal format */}
            {format === 'normal' && (
                <div className="mb-3">
                    <Form.Label>Padding</Form.Label>
                    <div className="d-flex gap-3">
                        <Form.Check
                            type="switch"
                            id="padding-top-switch"
                            label="Bovenkant"
                            checked={Boolean(componentSettings.padding_top)}
                            onChange={(e) => handleFieldChange('padding_top', e.target.checked)}
                        />
                        <Form.Check
                            type="switch"
                            id="padding-bottom-switch"
                            label="Onderkant"
                            checked={Boolean(componentSettings.padding_bottom)}
                            onChange={(e) => handleFieldChange('padding_bottom', e.target.checked)}
                            style={{marginLeft:'1.5rem'}}
                        />
                    </div>
                </div>
            )}
            
            {/* Background Image */}
            <Form.Group className="mb-3">
                <Form.Label>Achtergrond</Form.Label>
                <Form.Label style={{marginLeft:'1.5rem'}}>Positie</Form.Label>
                <Form.Label style={{marginLeft:'11rem'}}>Overlay kleur</Form.Label>
                {/* Line 1: Image selection button + Position controls */}
                <div className="d-flex align-items-center gap-2 mb-2">
                    <Button 
                        variant="outline-secondary" 
                        onClick={() => setShowAssetSelector(true)}
                        className="flex-shrink-0"
                        style={{ 
                            filter: 'grayscale(100%)',
                            minWidth: '35.2px',
                            height: '35.2px',
                            padding: '0',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginRight: '3.2rem'
                        }}
                    >
                        🖼️
                    </Button>
                    <div className="d-flex align-items-center gap-2">
                        <div className="d-flex align-items-center gap-1">
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-arrow-left-right" viewBox="0 0 16 16">
                                <path fillRule="evenodd" d="M1 11.5a.5.5 0 0 0 .5.5h11.793l-3.147 3.146a.5.5 0 0 0 .708.708l4-4a.5.5 0 0 0 0-.708l-4-4a.5.5 0 0 0-.708.708L13.293 11H1.5a.5.5 0 0 0-.5.5m14-7a.5.5 0 0 1-.5.5H2.707l3.147 3.146a.5.5 0 1 1-.708.708l-4-4a.5.5 0 0 1 0-.708l4-4a.5.5 0 1 1 .708.708L2.707 4H14.5a.5.5 0 0 1 .5.5"/>
                            </svg>
                            <Form.Control
                                type="number"
                                value={background_position_X}
                                onChange={(e) => handleFieldChange('background_position_X', parseInt(e.target.value))}
                                min="0"
                                max="100"
                                step="1"
                                placeholder="50"
                                style={{width: '60px'}}
                                disabled
                            />
                        </div>
                        <div className="d-flex align-items-center gap-1">
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-arrow-up-down" viewBox="0 0 16 16">
                                <path fillRule="evenodd" d="M11.5 15a.5.5 0 0 0 .5-.5V2.707l3.146 3.147a.5.5 0 0 0 .708-.708l-4-4a.5.5 0 0 0-.708 0l-4 4a.5.5 0 1 0 .708.708L11 2.707V14.5a.5.5 0 0 0 .5.5m-7-14a.5.5 0 0 1 .5.5v11.793l3.146-3.147a.5.5 0 0 1 .708.708l-4 4a.5.5 0 0 1-.708 0l-4-4a.5.5 0 1 1 .708-.708L4 13.293V1.5a.5.5 0 0 1 .5-.5"/>
                            </svg>
                            <Form.Control
                                type="number"
                                value={background_position_Y}
                                onChange={(e) => handleFieldChange('background_position_Y', parseInt(e.target.value))}
                                min="0"
                                max="100"
                                step="1"
                                placeholder="50"
                                style={{width: '60px'}}
                            />
                        </div>
                    </div>
                    <Form.Select
                        value={overlay_background_color}
                        onChange={(e) => handleFieldChange('overlay_background_color', e.target.value)}
                        style={{marginLeft:'1.5rem'}}
                    >
                        {overlayColorOptions.map(option => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                    </Form.Select>
                </div>
                {/* Line 2: Image URL */}
                <Form.Control
                    type="text"
                    value={background_image}
                    onChange={(e) => handleFieldChange('background_image', e.target.value)}
                    placeholder="Enter image URL"
                    className="mb-2 small-font-input"
                    disabled
                />
                {/* Line 3: Image preview */}
                {background_image && (
                    <div style={{overflow:'hidden',width:'100%',aspectRatio:'3/1',display:'flex',alignItems:'center',justifyContent:'center',position:'relative'}}>
                        <img 
                            src={background_image} 
                            alt="Background image preview" 
                            style={{ 
                                width: '100%', 
                                transform: `translate(${50 - background_position_X}%, ${50 - background_position_Y}%)`,
                                cursor: 'pointer',
                                border: '1px solid #dee2e6',
                                borderRadius: '4px'
                            }}
                            onClick={() => window.open(background_image, '_blank')}
                        />
                        {/* Overlay color preview */}
                        {overlay_background_color !== 'none' && (
                            <div 
                                style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    backgroundColor: overlayColorOptions.find(opt => opt.value === overlay_background_color)?.color || 'transparent',
                                    pointerEvents: 'none',
                                    borderRadius: '4px'
                                }}
                            />
                        )}
                    </div>
                )}
            </Form.Group>
            
            {/* Title */}
            <Form.Group className="mb-3">
                <Form.Label>Titel</Form.Label>
                <Form.Control
                    type="text"
                    value={title}
                    onChange={(e) => handleFieldChange('title', e.target.value)}
                    placeholder="Enter title"
                />
            </Form.Group>
            
            {/* Text Content with WYSIWYG Editor */}
            <Form.Group className="mb-3">
                <Form.Label>Tekst</Form.Label>
                
                {/* Rich Text Editor Toolbar */}
                <div className="border rounded-top p-2 bg-light">
                    <Button
                        variant="outline-secondary"
                        size="sm"
                        className="me-2"
                        onClick={() => formatText('bold')}
                        disabled={textSourceCodeMode}
                    >
                        <strong>B</strong>
                    </Button>
                    <Button
                        variant="outline-secondary"
                        size="sm"
                        className="me-2"
                        onClick={() => formatText('italic')}
                        disabled={textSourceCodeMode}
                    >
                        <em>I</em>
                    </Button>
                    <Button
                        variant="outline-secondary"
                        size="sm"
                        className="me-2"
                        onClick={() => formatText('underline')}
                        disabled={textSourceCodeMode}
                    >
                        <u>U</u>
                    </Button>
                    <Button
                        variant="outline-secondary"
                        size="sm"
                        className="me-2"
                        onClick={() => formatText('insertUnorderedList')}
                        disabled={textSourceCodeMode}
                    >
                        • List
                    </Button>
                    <Button
                        variant="outline-secondary"
                        size="sm"
                        className="me-2"
                        onClick={() => formatText('insertOrderedList')}
                        disabled={textSourceCodeMode}
                    >
                        1. List
                    </Button>
                    <Button
                        variant="outline-secondary"
                        size="sm"
                        className="me-2"
                        onClick={() => {
                            const url = prompt('Enter URL:');
                            if (url) formatText('createLink', url);
                        }}
                        disabled={textSourceCodeMode}
                    >
                        🔗 Link
                    </Button>
                    <Button
                        variant={textSourceCodeMode ? "primary" : "outline-secondary"}
                        size="sm"
                        className="ms-auto"
                        onClick={handleSourceCodeToggle}
                    >
                        {textSourceCodeMode ? "WYSIWYG" : "</>"}
                    </Button>
                </div>
                
                {/* Editor Area */}
                {textSourceCodeMode ? (
                    <Form.Control
                        ref={textEditorRef}
                        as="textarea"
                        rows={6}
                        value={text_content}
                        onChange={(e) => handleFieldChange('text_content', e.target.value)}
                        style={{
                            fontFamily: 'monospace',
                            fontSize: '0.9rem',
                            borderTop: 'none',
                            borderTopLeftRadius: '0',
                            borderTopRightRadius: '0'
                        }}
                        placeholder="Voer hier de HTML-code in..."
                    />
                ) : (
                    <div
                        ref={textEditorRef}
                        contentEditable="true"
                        onInput={handleTextChange}
                        onClick={handleTextEditorClick}
                        className="rich-text-editor"
                        style={{
                            minHeight: '150px',
                            maxHeight: '300px',
                            overflowY: 'auto',
                            border: '1px solid #ced4da',
                            borderTop: 'none',
                            borderBottomLeftRadius: '0.25rem',
                            borderBottomRightRadius: '0.25rem',
                            padding: '0.75rem',
                            backgroundColor: 'white',
                            outline: 'none'
                        }}
                        suppressContentEditableWarning={true}
                    />
                )}
            </Form.Group>

            {/* Button Section */}
            <h6 className="mb-2 mt-4" style={{ borderBottom: '1px solid #dee2e6', paddingBottom: '8px' }}>
                Knop (optioneel)
            </h6>
            
            {/* Button Label and Color on same line */}
            <div className="row mb-3">
                <div className="col-8">
                    <Form.Group>
                        <Form.Label>Label</Form.Label>
                        <Form.Control
                            type="text"
                            value={button_label}
                            onChange={(e) => handleFieldChange('button_label', e.target.value)}
                            placeholder="Enter button label"
                        />
                    </Form.Group>
                </div>
                <div className="col-4">
                    <Form.Group>
                        <Form.Label>Kleur</Form.Label>
                        <Form.Select
                            value={button_color}
                            onChange={(e) => handleFieldChange('button_color', e.target.value)}
                        >
                            <option value="default">Groen</option>
                            <option value="highlight">Oranje</option>
                        </Form.Select>
                    </Form.Group>
                </div>
            </div>
            
            {/* Button Link */}
            <Form.Group className="mb-3">
                <Form.Label>Link</Form.Label>
                <Form.Control
                    type="text"
                    value={button_link}
                    onChange={(e) => handleFieldChange('button_link', e.target.value)}
                    placeholder="Enter button link"
                />
            </Form.Group>
             
            {/* Asset Selector Dialog */}
            <UkiesAssets
                open={showAssetSelector}
                setOpen={setShowAssetSelector}
                onAssetSelect={handleAssetSelect}
                token={apiToken}
            />
        </div>
    );
}